import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { money, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Page, PageHeader, StatusBadge } from "@/components/Bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cube, CheckCircle, WarningCircle, Info, Plus, SignOut, FilePdf, FileXls, ChartLineUp, MagnifyingGlass, X } from "@phosphor-icons/react";

const today = () => new Date().toISOString().slice(0, 10);

export default function FixedAssets() {
  const [status, setStatus] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [disposeTarget, setDisposeTarget] = useState(null);
  const [unclassified, setUnclassified] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [faSearch, setFaSearch] = useState(() => new URLSearchParams(window.location.search).get("code") || "");
  const [faStatus, setFaStatus] = useState("All");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/fixed-assets/status").then(({ data }) => setStatus(data)).catch(() => setStatus(null)),
      api.get("/register").then(({ data }) => setRows((data.register || []).filter((r) => r.category === "DEPRECIATION"))).catch(() => setRows([])),
    ]).finally(() => setLoading(false));
    api.get("/fixed-assets/code-suggestions").then(({ data }) => setUnclassified(data.suggestions || [])).catch(() => {});
    api.get("/forecast?type=DEPRECIATION").then(({ data }) => setForecast(data)).catch(() => setForecast(null));
  }, []);

  useEffect(() => { load(); }, [load]);

  const inUse = status?.register_in_use;

  const faQ = faSearch.trim().toLowerCase();
  const visibleRows = rows.filter((r) => {
    const st = r.disposed ? "Disposed" : r.status;
    if (faStatus !== "All" && st !== faStatus) return false;
    if (faQ && !(`${r.description || ""} ${r.doc_number || ""} ${r.source_account_code || ""}`.toLowerCase().includes(faQ))) return false;
    return true;
  });

  const downloadReport = async (kind) => {
    try {
      const res = await api.get(`/fixed-assets/register/${kind}`, { responseType: "blob" });
      const mime = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `fixed-asset-register-${new Date().toISOString().slice(0, 10)}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Fixed Asset Register ${kind.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Could not generate ${kind.toUpperCase()}`);
    }
  };

  return (
    <Page>
      <PageHeader
        overline="Fixed Assets"
        title="Depreciation"
        subtitle="Detects whether fixed assets are managed in Xero — and manages depreciation here when they're not."
      >
        <div className="flex items-center gap-2.5 flex-wrap">
          {rows.length > 0 && (
            <>
              <button onClick={() => downloadReport("pdf")} data-testid="fa-export-pdf-btn"
                className="flex items-center gap-2 border border-slate-200 text-navy px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-navy hover:text-white transition-colors duration-200">
                <FilePdf size={16} weight="fill" /> Register PDF
              </button>
              <button onClick={() => downloadReport("excel")} data-testid="fa-export-excel-btn"
                className="flex items-center gap-2 border border-slate-200 text-navy px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-navy hover:text-white transition-colors duration-200">
                <FileXls size={16} weight="fill" /> Register Excel
              </button>
            </>
          )}
          {status && !inUse && (
            <button onClick={() => setShowModal(true)} data-testid="add-depreciation-btn"
              className="flex items-center gap-2 bg-orange text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-navy transition-colors duration-200">
              <Plus size={16} weight="bold" /> New depreciating asset
            </button>
          )}
        </div>
      </PageHeader>

      {!inUse && unclassified.length > 0 && (
        <Link to="/settings?tab=codes" data-testid="fa-unclassified-hint"
          className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 mb-5 hover:border-orange hover:bg-orange/[0.04] transition-colors group">
          <WarningCircle size={20} weight="fill" className="text-warn shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-navy">
              {unclassified.length} account{unclassified.length > 1 ? "s" : ""} not yet classified
            </span>
            <span className="text-sm text-navy/70">
              {" "}— accounts that look like fixed-asset/depreciation accounts aren't tagged ({unclassified.slice(0, 3).map((s) => s.code).join(", ")}{unclassified.length > 3 ? "…" : ""}).
            </span>
          </div>
          <span className="text-sm font-medium text-orangeink shrink-0 group-hover:underline">Classify in Settings →</span>
        </Link>
      )}

      {/* Detection verdict */}
      {status && (
        <div data-testid="fa-status-banner"
          className={`rounded-lg border p-5 mb-7 flex items-start gap-4 ${
            inUse ? "bg-positive/[0.06] border-positive/30"
            : status.scope_granted === false ? "bg-warn/[0.07] border-warn/40"
            : "bg-orange/[0.06] border-orange/30"}`}>
          <div className="mt-0.5">
            {inUse ? <CheckCircle size={26} weight="fill" className="text-positive" />
              : status.scope_granted === false ? <WarningCircle size={26} weight="fill" className="text-warn" />
              : <Info size={26} weight="fill" className="text-orangeink" />}
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-lg text-navy tracking-tight" data-testid="fa-status-headline">
              {inUse
                ? "Fixed assets are managed in Xero — Lottie won't touch them"
                : status.scope_granted === false
                ? "Fixed Asset register access not granted"
                : "Xero isn't depreciating fixed assets — manage them in Lottie?"}
            </div>
            <p className="text-sm text-navy/65 mt-1" data-testid="fa-status-reason">{status.reason}</p>
            {status.available && (
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-navy/70 font-mono">
                <span data-testid="fa-registered-count">Registered assets: {status.registered_count}</span>
                <span>Draft: {status.draft_count}</span>
                <span>Last depreciation in Xero: {status.last_depreciation_date ? fmtDate(status.last_depreciation_date) : "—"}</span>
                <span>Managed in Lottie: {status.managed_in_lottie}</span>
              </div>
            )}
            {status.scope_granted === false && (
              <p className="text-xs text-warn mt-2">
                Add the <span className="font-mono font-semibold">assets.read</span> scope to your Xero app and reconnect (Settings → Connections) to enable register detection.
              </p>
            )}
            {status.mocked && (
              <span className="inline-block mt-2 text-[0.65rem] uppercase tracking-wider font-semibold text-navy/65 bg-navy/5 border border-slate-200 rounded-md px-2 py-0.5">Demo data</span>
            )}
          </div>
        </div>
      )}

      {/* Depreciation forecast — next 12 months */}
      {rows.length > 0 && forecast && (forecast.totals?.months || []).some((v) => Math.abs(v) > 0.005) && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden mb-7" data-testid="fa-forecast">
          <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-2">
            <ChartLineUp size={18} className="text-navy/70" />
            <span className="font-heading font-bold text-navy tracking-tight">Depreciation forecast</span>
            <span className="text-slate-600 text-sm">next {(forecast.months || []).length} months</span>
            <span className="ml-auto text-sm text-navy/70">
              Total projected: <span className="font-semibold text-navy tabular" data-testid="fa-forecast-total">{money((forecast.totals?.months || []).reduce((s, v) => s + v, 0))}</span>
            </span>
          </div>
          <div className="px-5 py-5 overflow-x-auto">
            <div className="flex items-end gap-3 min-w-[640px] h-36">
              {(forecast.months || []).map((m, i) => {
                const vals = forecast.totals?.months || [];
                const max = Math.max(1, ...vals.map((v) => Math.abs(v)));
                const v = vals[i] || 0;
                const h = Math.round((Math.abs(v) / max) * 100);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1.5" data-testid={`fa-forecast-bar-${i}`}>
                    <span className="text-[0.6rem] text-navy/50 tabular">{v ? money(v).replace(/\.00$/, "") : ""}</span>
                    <div className="w-full bg-slate-100 rounded-md flex items-end" style={{ height: "100%" }}>
                      <div className="w-full bg-orange/70 rounded-md transition-all" style={{ height: `${h}%` }} />
                    </div>
                    <span className="text-[0.6rem] text-navy/50 whitespace-nowrap">{String(m).slice(0, 6)}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-navy/45 mt-4">Projected monthly depreciation from your managed schedules. Reflects planned release journals — actual postings may vary if assets are disposed early.</p>
          </div>
        </div>
      )}

      {/* Managed depreciation schedules */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <Cube size={18} className="text-navy/70" />
          <span className="font-heading font-bold text-navy tracking-tight">Depreciating assets in Lottie</span>
          <span className="text-slate-600 text-sm">({rows.length === visibleRows.length ? rows.length : `${visibleRows.length} of ${rows.length}`})</span>
          {rows.length > 0 && (
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="relative">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-navy/40" />
                <input value={faSearch} onChange={(e) => setFaSearch(e.target.value)} data-testid="fa-search"
                  placeholder="Search asset, code…"
                  className="w-52 pl-8 pr-7 py-2 text-sm rounded-lg border border-slate-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
                {faSearch && (
                  <button onClick={() => setFaSearch("")} data-testid="fa-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy"><X size={14} /></button>
                )}
              </div>
              <select value={faStatus} onChange={(e) => setFaStatus(e.target.value)} data-testid="fa-status-filter"
                className="py-2 px-3 text-sm rounded-lg border border-slate-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none text-navy">
                {["All", "Active", "Completed", "Disposed"].map((s) => <option key={s} value={s}>{s === "All" ? "All statuses" : s}</option>)}
              </select>
            </div>
          )}
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center text-slate-600 font-mono text-sm">LOADING…</div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center" data-testid="fa-empty">
            <Cube size={36} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 text-sm">No depreciating assets yet.{!inUse && status ? " Add one to start posting monthly depreciation journals." : ""}</p>
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="px-5 py-12 text-center" data-testid="fa-no-matches">
            <MagnifyingGlass size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 text-sm">No assets match your search or filter.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="fa-table">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200 text-[0.66rem] uppercase tracking-wider text-slate-600">
                <th className="text-left px-5 py-3 font-semibold">Asset</th>
                <th className="text-left px-5 py-3 font-semibold">Method</th>
                <th className="text-right px-5 py-3 font-semibold">Cost</th>
                <th className="text-right px-5 py-3 font-semibold">Depreciated</th>
                <th className="text-right px-5 py-3 font-semibold">Net book value</th>
                <th className="text-right px-5 py-3 font-semibold">Ends</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const cost = r.depreciation?.cost ?? r.original_amount;
                const residual = r.depreciation?.residual ?? 0;
                const nbv = Math.round((cost - r.released_amount) * 100) / 100;
                const disposed = r.disposed;
                const gl = r.disposal?.gain_loss ?? 0;
                return (
                  <tr key={r.schedule_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`fa-row-${r.schedule_id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-navy">{r.description || r.doc_number}</div>
                      <div className="text-xs text-navy/65 font-mono">{r.doc_number} · {r.source_account_code} → {r.pl_account_code}</div>
                    </td>
                    <td className="px-5 py-3 text-navy/70 capitalize">{(r.depreciation?.method || "straight_line").replace("_", "-")}</td>
                    <td className="px-5 py-3 text-right font-mono tabular text-navy">{money(cost)}</td>
                    <td className="px-5 py-3 text-right font-mono tabular text-navy">{money(r.released_amount)}</td>
                    <td className="px-5 py-3 text-right font-mono tabular text-navy">{disposed ? "—" : money(nbv)}</td>
                    <td className="px-5 py-3 text-right font-mono text-navy/70">{fmtDate(r.final_date)}</td>
                    <td className="px-5 py-3"><StatusBadge value={r.status} testid={`fa-status-${r.schedule_id}`} /></td>
                    <td className="px-5 py-3 text-right">
                      {disposed ? (
                        <span className={`text-xs font-mono ${gl >= 0 ? "text-positive" : "text-negative"}`} data-testid={`fa-gainloss-${r.schedule_id}`}>
                          {gl >= 0 ? "Gain" : "Loss"} {money(Math.abs(gl))}
                        </span>
                      ) : (
                        <button onClick={() => setDisposeTarget(r)} data-testid={`fa-dispose-btn-${r.schedule_id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-navy/70 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-navy hover:text-white transition-colors">
                          <SignOut size={13} /> Dispose
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <DepreciationModal open={showModal} onClose={() => setShowModal(false)}
        accounts={status?.accounts} onCreated={load} />
      <DisposeModal target={disposeTarget} onClose={() => setDisposeTarget(null)} onDisposed={load} />
    </Page>
  );
}

function DepreciationModal({ open, onClose, accounts, onCreated }) {
  const fa = accounts || { fixed_assets: [], accumulated_depreciation: [], depreciation_expense: [] };
  const [name, setName] = useState("");
  const [reference, setReference] = useState("");
  const [cost, setCost] = useState("");
  const [residual, setResidual] = useState("0");
  const [start, setStart] = useState(today());
  const [years, setYears] = useState("3");
  const [method, setMethod] = useState("straight_line");
  const [rate, setRate] = useState("25");
  const [faCode, setFaCode] = useState("");
  const [accumCode, setAccumCode] = useState("");
  const [expCode, setExpCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFaCode(fa.fixed_assets[0]?.code || "");
    setAccumCode(fa.accumulated_depreciation[0]?.code || "");
    setExpCode(fa.depreciation_expense[0]?.code || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const months = Math.round(Number(years) * 12);
  const body = {
    asset_name: name, reference: reference.trim(), cost: Number(cost), residual: Number(residual) || 0,
    start_date: start, useful_life_months: months, method, annual_rate: Number(rate) || 0,
    accum_dep_code: accumCode, dep_expense_code: expCode, fixed_asset_code: faCode,
  };

  const loadPreview = useCallback(async () => {
    if (!Number(cost) || Number(cost) <= 0 || !months || !accumCode || !expCode) { setPreview(null); return; }
    if (method === "reducing_balance" && !(Number(rate) > 0)) { setPreview(null); return; }
    try {
      const { data } = await api.post("/fixed-assets/depreciation/preview", body);
      setPreview(data.preview);
    } catch { setPreview(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cost, residual, start, years, method, rate, accumCode, expCode]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(loadPreview, 250);
    return () => clearTimeout(t);
  }, [loadPreview, open]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Enter an asset name"); return; }
    if (!Number(cost) || Number(cost) <= 0) { toast.error("Enter the asset cost"); return; }
    if (!accumCode || !expCode) { toast.error("Select the depreciation account codes"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/fixed-assets/depreciation", body);
      toast.success(`Depreciation scheduled — ${data.periods} monthly journals queued`);
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create depreciation schedule");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-lg border-slate-200 p-0 gap-0 max-h-[92vh] overflow-y-auto" data-testid="depreciation-dialog">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
          <div className="overline text-orangeink mb-1">New depreciating asset</div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter text-navy">Set up depreciation</DialogTitle>
          <DialogDescription className="text-navy/70">
            Schedules monthly journals: Dr Depreciation Expense, Cr Accumulated Depreciation. The asset cost is assumed already recorded in Xero — no set-up journal is posted.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-7 py-6">
          <div className="md:col-span-2">
            <Lbl>Asset name</Lbl>
            <input value={name} data-testid="dep-name" onChange={(e) => setName(e.target.value)} placeholder="e.g. MacBook Pro 16″"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>
          <div>
            <Lbl>Reference</Lbl>
            <input value={reference} data-testid="dep-reference" onChange={(e) => setReference(e.target.value)} placeholder="e.g. FA-2026-001"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>
          <div>
            <Lbl>Depreciation starts</Lbl>
            <input type="date" value={start} data-testid="dep-start" onChange={(e) => setStart(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>

          <div>
            <Lbl>Asset cost</Lbl>
            <input type="number" min="0" step="0.01" value={cost} data-testid="dep-cost" onChange={(e) => setCost(e.target.value)} placeholder="0.00"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>
          <div>
            <Lbl>Residual / salvage value</Lbl>
            <input type="number" min="0" step="0.01" value={residual} data-testid="dep-residual" onChange={(e) => setResidual(e.target.value)} placeholder="0.00"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          </div>

          <div>
            <Lbl>Useful life (years)</Lbl>
            <input type="number" min="0.5" step="0.5" value={years} data-testid="dep-years" onChange={(e) => setYears(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
            <p className="text-xs text-navy/65 mt-1">{months} monthly periods</p>
          </div>
          <div>
            <Lbl>Method</Lbl>
            <div className="flex border border-slate-200 rounded-lg overflow-hidden text-sm">
              {[{ v: "straight_line", l: "Straight-line" }, { v: "reducing_balance", l: "Reducing balance" }].map((m) => (
                <button key={m.v} type="button" data-testid={`dep-method-${m.v}`} onClick={() => setMethod(m.v)}
                  className={`px-4 py-2 font-medium transition-colors border-l border-slate-100 first:border-l-0 ${
                    method === m.v ? "bg-orange text-white" : "bg-white text-navy/70 hover:bg-navy/5"}`}>
                  {m.l}
                </button>
              ))}
            </div>
          </div>

          {method === "reducing_balance" && (
            <div>
              <Lbl>Annual rate (%)</Lbl>
              <input type="number" min="0" step="0.1" value={rate} data-testid="dep-rate" onChange={(e) => setRate(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
            </div>
          )}

          <div className={method === "reducing_balance" ? "" : "md:col-span-2"}>
            <Lbl>Fixed-asset account (info)</Lbl>
            <Select value={faCode} onValueChange={setFaCode}>
              <SelectTrigger data-testid="dep-fa-code" className="rounded-lg border-slate-200"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>{fa.fixed_assets.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Lbl>Accumulated Depreciation (BS)</Lbl>
            <Select value={accumCode} onValueChange={setAccumCode}>
              <SelectTrigger data-testid="dep-accum-code" className="rounded-lg border-slate-200"><SelectValue placeholder="Select code" /></SelectTrigger>
              <SelectContent>
                {fa.accumulated_depreciation.length === 0 && <div className="px-3 py-2 text-xs text-navy/70">No accumulated-depreciation account found.</div>}
                {fa.accumulated_depreciation.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Lbl>Depreciation Expense (P&amp;L)</Lbl>
            <Select value={expCode} onValueChange={setExpCode}>
              <SelectTrigger data-testid="dep-exp-code" className="rounded-lg border-slate-200"><SelectValue placeholder="Select code" /></SelectTrigger>
              <SelectContent>
                {fa.depreciation_expense.length === 0 && <div className="px-3 py-2 text-xs text-navy/70">No depreciation-expense account found.</div>}
                {fa.depreciation_expense.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {preview && (
          <div className="px-7 pb-5">
            <div className="bg-navy/[0.03] border border-slate-200 rounded-lg p-4 flex items-center justify-between">
              <div className="overline text-navy/65">Calculated depreciation</div>
              <div className="text-sm font-mono text-navy/70" data-testid="dep-preview">
                {preview.periods} months · depreciable {money(preview.depreciable)} · first charge {money(preview.per_period_amount)} · ends {fmtDate(preview.final_date)}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 px-7 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={onClose} data-testid="dep-cancel-btn"
            className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="dep-submit-btn"
            className="px-6 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors duration-200 disabled:opacity-60">
            {saving ? "SCHEDULING…" : "Schedule depreciation"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Lbl({ children }) { return <span className="overline text-navy/70 block mb-1.5">{children}</span>; }

function DisposeModal({ target, onClose, onDisposed }) {
  const open = !!target;
  const [allAccounts, setAllAccounts] = useState([]);
  const [date, setDate] = useState(today());
  const [proceeds, setProceeds] = useState("0");
  const [proceedsCode, setProceedsCode] = useState("");
  const [glCode, setGlCode] = useState("");
  const [faCode, setFaCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(today()); setProceeds("0"); setProceedsCode(""); setGlCode(""); setSaving(false);
    setFaCode(target?.depreciation?.fixed_asset_code || "");
    api.get("/accounts").then(({ data }) => setAllAccounts(data.accounts || [])).catch(() => setAllAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const cost = target.depreciation?.cost ?? target.original_amount;
  const accumulated = target.released_amount;
  const nbv = Math.round((cost - accumulated) * 100) / 100;
  const proceedsNum = Number(proceeds) || 0;
  const gainLoss = Math.round((proceedsNum - nbv) * 100) / 100;
  const plAccounts = allAccounts.filter((a) => ["EXPENSE", "REVENUE"].includes(a.class));
  const assetAccounts = allAccounts.filter((a) => a.class === "ASSET");

  const submit = async () => {
    if (!faCode) { toast.error("Select the fixed-asset cost account"); return; }
    if (!glCode) { toast.error("Select the gain/loss on disposal account"); return; }
    if (proceedsNum > 0 && !proceedsCode) { toast.error("Select where the proceeds are received"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/fixed-assets/${target.schedule_id}/dispose`, {
        date, proceeds: proceedsNum, proceeds_account_code: proceedsCode,
        gain_loss_account_code: glCode, fixed_asset_code: faCode,
      });
      const v = data.gain_loss >= 0 ? `gain of ${money(data.gain_loss)}` : `loss of ${money(Math.abs(data.gain_loss))}`;
      toast.success(`Asset disposed — ${v} posted to Xero`);
      onDisposed?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not dispose asset");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-lg border-slate-200 p-0 gap-0 max-h-[92vh] overflow-y-auto" data-testid="dispose-dialog">
        <DialogHeader className="px-7 pt-6 pb-4 border-b border-slate-100">
          <div className="overline text-orangeink mb-1">Dispose asset</div>
          <DialogTitle className="font-heading font-bold text-2xl tracking-tighter text-navy">{target.description || target.doc_number}</DialogTitle>
          <DialogDescription className="text-navy/70">
            Stops future depreciation and posts the disposal journal: removes the cost and accumulated depreciation, records the proceeds, and books the gain or loss.
          </DialogDescription>
        </DialogHeader>

        <div className="px-7 py-5">
          <div className="grid grid-cols-3 gap-3 mb-5" data-testid="dispose-summary">
            <Stat label="Cost" value={money(cost)} />
            <Stat label="Accum. depreciation" value={money(accumulated)} />
            <Stat label="Net book value" value={money(nbv)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Lbl>Disposal date</Lbl>
              <input type="date" value={date} data-testid="dispose-date" onChange={(e) => setDate(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
            </div>
            <div>
              <Lbl>Sale proceeds</Lbl>
              <input type="number" min="0" step="0.01" value={proceeds} data-testid="dispose-proceeds" onChange={(e) => setProceeds(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
              <p className="text-xs text-navy/65 mt-1">Enter 0 if the asset is scrapped.</p>
            </div>

            <div>
              <Lbl>Fixed-asset cost account (Cr)</Lbl>
              <Select value={faCode} onValueChange={setFaCode}>
                <SelectTrigger data-testid="dispose-fa-code" className="rounded-lg border-slate-200"><SelectValue placeholder="Select code" /></SelectTrigger>
                <SelectContent>{assetAccounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Lbl>Proceeds received into</Lbl>
              <Select value={proceedsCode} onValueChange={setProceedsCode}>
                <SelectTrigger data-testid="dispose-proceeds-code" className="rounded-lg border-slate-200"><SelectValue placeholder={proceedsNum > 0 ? "Select code" : "Not needed"} /></SelectTrigger>
                <SelectContent>{allAccounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Lbl>Gain / loss on disposal account (P&amp;L)</Lbl>
              <Select value={glCode} onValueChange={setGlCode}>
                <SelectTrigger data-testid="dispose-gl-code" className="rounded-lg border-slate-200"><SelectValue placeholder="Select code" /></SelectTrigger>
                <SelectContent>{plAccounts.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className={`mt-5 rounded-lg border p-4 flex items-center justify-between ${gainLoss >= 0 ? "bg-positive/[0.06] border-positive/30" : "bg-negative/[0.06] border-negative/30"}`} data-testid="dispose-gainloss-preview">
            <span className="overline text-navy/70">{gainLoss >= 0 ? "Gain on disposal" : "Loss on disposal"}</span>
            <span className={`font-mono font-semibold text-lg ${gainLoss >= 0 ? "text-positive" : "text-negative"}`}>
              {gainLoss >= 0 ? "+" : "−"}{money(Math.abs(gainLoss))}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-7 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button onClick={onClose} data-testid="dispose-cancel-btn"
            className="px-5 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="dispose-submit-btn"
            className="px-6 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors duration-200 disabled:opacity-60">
            {saving ? "POSTING…" : "Dispose & post to Xero"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-navy/[0.03] border border-slate-200 rounded-lg px-3 py-2.5">
      <div className="overline text-navy/65">{label}</div>
      <div className="font-mono font-semibold text-navy tabular">{value}</div>
    </div>
  );
}
